import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PushNotifications } from '@capacitor/push-notifications';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useMyRsvps } from '../hooks/queries/useMyRsvps';
import { useFindGroups } from '../hooks/queries/useFindGroups';
import { isWelcomeDemo, welcomeDone } from '../lib/welcomeDemo';
import {
  pushAsked,
  pushPermission,
  pushSupported,
  registerIfGranted,
  requestPush,
  saveToken,
} from '../lib/push';
import { getNotificationTarget } from '../utils/notifications';
import { appLog } from '../lib/appLog';

/**
 * Push on the phone (round 6, 2026-10-27): the token goes to the signed-in user, a tapped push opens what it is about,
 * and the iOS question is asked once - after you are going to something or in a group and past the welcome steps, when
 * a push has something to tell you, never on first open.
 */
export default function PushHandler() {
  const { session, authReady } = useCurrentUserProfile();
  const user = session?.user ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const supported = pushSupported();
  // navigate changes with every page, and as a dependency it re-ran the listeners' effect on each one: a fresh iOS token
  // request and a register_push_token call per tap (190 a day on Sam's phone, 2026-10-30). The ref keeps it once per user.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Listeners, once per signed-in user.
  useEffect(() => {
    if (!supported || !user?.id) return undefined;
    const subs = [
      PushNotifications.addListener('registration', ({ value }) => {
        saveToken(value).catch((err) =>
          appLog('warn', 'push.token_save_failed', { message: err?.message }),
        );
      }),
      PushNotifications.addListener('registrationError', (err) =>
        appLog('warn', 'push.register_failed', { message: err?.error ?? String(err) }),
      ),
      PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
        const n = notification?.data?.n;
        appLog('info', 'push.opened', { type: n?.type ?? null });
        navigateRef.current(getNotificationTarget(n) ?? '/notifications');
      }),
    ];
    // Allowed already: the token can change between launches, so it is fetched and saved again.
    void registerIfGranted();
    return () => {
      subs.forEach((s) => s.then((h) => h.remove()).catch(() => {}));
    };
  }, [supported, user?.id]);

  // The one ask.
  const eligible = supported && authReady && Boolean(user?.id) && !pushAsked();
  const { data: rsvps = [] } = useMyRsvps(eligible ? user.id : null, { enabled: eligible });
  const { data: groups = [] } = useFindGroups(eligible ? user.id : null, { authReady: eligible });
  const joined = rsvps.some((r) => r.status === 'going') || groups.length > 0;
  const ready = eligible && joined && welcomeDone(user) && !isWelcomeDemo(location.search);
  useEffect(() => {
    if (!ready) return undefined;
    const t = window.setTimeout(async () => {
      if (document.querySelector('[role="dialog"]')) return; // never over a sheet; the next page tries again
      if ((await pushPermission()) !== 'prompt') return;
      const answer = await requestPush();
      appLog('info', 'push.permission', { answer });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [ready, location.pathname]);

  return null;
}
